import type { ItemId } from './inventory';

// Data-driven consumables: one entry per edible/usable item. Eating + cooking both read this table, so
// adding a food is a data edit, not code. Positive hp/hunger/thirst RESTORE; raw meat carries poisonSec
// (HP drain over time, no HP gain); cooked food gives the most HP. cookedInto + cookTimeSec drive the
// campfire. `bandage` items are used (stop bleeding) rather than eaten. Forgiving values.

export interface ConsumableDef {
  hp?: number;        // buffered into the slow pending-heal (the gain marker)
  hunger?: number;    // restored immediately
  thirst?: number;    // restored immediately
  poisonSec?: number; // raw food: seconds of poison applied on eating
  cookTimeSec?: number; // if set, this is a RAW item the campfire can cook
  cookedInto?: ItemId;  // what it becomes when cooked
  bandage?: boolean;  // used as a bandage (stops bleeding) instead of eaten
}

export const CONSUMABLES: Partial<Record<ItemId, ConsumableDef>> = {
  // Plants -------------------------------------------------------------------------------------------
  mushroom: { hunger: 8, hp: 2, cookTimeSec: 6, cookedInto: 'mushroom_cooked' },
  mushroom_cooked: { hunger: 18, hp: 9 },
  cactus_flesh: { thirst: 22, hunger: 3, cookTimeSec: 6, cookedInto: 'cactus_cooked' }, // raw = mostly WATER
  cactus_cooked: { thirst: 10, hunger: 16, hp: 4 }, // cooked = less water, more food
  pumpkin: { hunger: 18, thirst: 6, cookTimeSec: 7, cookedInto: 'pumpkin_cooked' },
  pumpkin_cooked: { hunger: 32, hp: 9 },
  corn: { hunger: 5, cookTimeSec: 7, cookedInto: 'corn_cooked' }, // raw is weak; cooking is the point
  corn_cooked: { hunger: 26, hp: 10 },
  berries: { hunger: 12, hp: 3, thirst: 5 }, // farmed-only snack: eaten raw, juicy (no cooked form)
  coconut: { thirst: 25, hunger: 6 },        // palm drop: mostly water, a little food (owner)
  apple: { hunger: 14, hp: 3, thirst: 4 },   // rare tree fruit: a crisp, refreshing snack
  // Meat: raw POISONS (no HP), cooked gives the most HP -------------------------------------------------
  meat_deer_raw: { hunger: 12, poisonSec: 9, cookTimeSec: 10, cookedInto: 'meat_deer_cooked' },
  meat_deer_cooked: { hunger: 32, hp: 16 },
  meat_chicken_raw: { hunger: 9, poisonSec: 9, cookTimeSec: 8, cookedInto: 'meat_chicken_cooked' },
  meat_chicken_cooked: { hunger: 24, hp: 12 },
  meat_boar_raw: { hunger: 13, poisonSec: 10, cookTimeSec: 11, cookedInto: 'meat_boar_cooked' },
  meat_boar_cooked: { hunger: 36, hp: 18 },
  meat_cow_raw: { hunger: 14, poisonSec: 10, cookTimeSec: 12, cookedInto: 'meat_cow_cooked' },
  meat_cow_cooked: { hunger: 40, hp: 22 },
  // Other --------------------------------------------------------------------------------------------
  bandage: { bandage: true, hp: 4 },
};

export function consumableOf(id: ItemId): ConsumableDef | undefined {
  return CONSUMABLES[id];
}
export function isConsumable(id: ItemId): boolean {
  return CONSUMABLES[id] !== undefined;
}
// A FOOD is an eaten consumable (not a bandage). Used for Rust-style hotbar quick-eat: pressing a food slot's
// number key eats one straight away, while a bandage stays a deliberate wield-and-use.
export function isFood(id: ItemId): boolean {
  const d = CONSUMABLES[id];
  return d !== undefined && !d.bandage;
}
// Raw items the campfire/furnace can cook -> [cookedId, seconds].
export function cookResult(id: ItemId): { into: ItemId; sec: number } | null {
  const d = CONSUMABLES[id];
  return d && d.cookedInto && d.cookTimeSec ? { into: d.cookedInto, sec: d.cookTimeSec } : null;
}

// What the FURNACE smelts (input + wood fuel over time), alongside cooking raw food (cookResult).
// Metal ore (mined from metal nodes) smelts down 1:1 into Metal, the mid-tier build/craft resource.
export function smeltResult(id: ItemId): { into: ItemId; sec: number; need?: number } | null {
  if (id === 'metal_ore') return { into: 'metal', sec: 6 };
  return null;
}

// What the OIL REFINERY converts (input + wood fuel over time). Animal fat renders down into light fuel;
// smelted metal concentrates further into refined metal, the reinforced build tier's cost (BUILD.cost in
// config). `need` = inputs consumed per output (default 1): refined metal is a 10:1 concentrate on purpose,
// so a reinforced piece (30 refined, see BUILD.cost) really costs 300 metal plus fuel and refinery time.
export function refineResult(id: ItemId): { into: ItemId; sec: number; need?: number } | null {
  if (id === 'fat') return { into: 'light_fuel', sec: 10 };
  if (id === 'metal') return { into: 'refined_metal', sec: 15, need: 10 };
  return null;
}
